import { Stack, Text } from '@mantine/core';
import { FileStatusTypeId } from '@lib2/model/media/FileStatusTypeObj';
import { PfileTypeId } from '@lib2/model/pfile/PFileType';
import type { PfileRow } from '@lib2/model/pfile/PfileTypes';
import { getPfiles } from '@lib2/model/pfile/PfileUtil';
import { useSharedLinkConfig } from '@/features/link/useLinkConfig';
import { LinkUtils } from '@/features/link/LinkUtils';
import { AppTvBillboard } from '@/features/apps/components/AppTvBillboard';
import type { AppV1Row, AppV1Theme } from '@/lib/types/AppV1';

/**
 * The shells' default dark preset (jvm AppV1Assembler.PresetMidnight): the
 * series editor is account-level and has no app to read a theme from, so the
 * hero preview here shows art placement on the default ground; the App
 * Preview tab renders the real theme.
 */
export const PREVIEW_THEME_MIDNIGHT: AppV1Theme = {
  presetId: 'midnight',
  mode: 'dark',
  colors: {
    bgPrimary: '#0B0E14',
    bgSurface: '#151A24',
    bgOverlay: '#0B0E14CC',
    textPrimary: '#FFFFFF',
    textSecondary: '#A8B0BF',
    textOnAccent: '#0B0E14',
    accent: '#E8B84B',
    scrim: '#0B0E14',
    focusRing: '#FFFFFF',
    cardBorderFocused: '#FFFFFF',
    cardBorderUnfocused: '#00000000',
    badgeLive: '#E4002B',
    badgeNew: '#E8B84B',
    progressFill: '#E8B84B',
    progressTrack: '#3A4152',
  },
  typography: { family: 'system', scale: 1 },
  spacing: { safeAreaPct: 5, cardGapPx: 24 },
  focus: { scale: 1.1, ringWidthPx: 4 },
};

interface SeriesHeroPreviewProps {
  llId: number;
  title: string;
  descShort: string | null;
  files: PfileRow[];
}

function readyPfile(files: PfileRow[], type: PfileTypeId): PfileRow | null {
  return (
    getPfiles(files, [type]).find((f) => f.fileStatusTypeId === FileStatusTypeId.Ready && !f.deleted) ??
    null
  );
}

/**
 * How the series reads on Home's hero (design ADR-0013, ADR-0016): the raw
 * poster as the Card style's art on the default dark ground (the series editor
 * has no app, so no hero style or theme to read) with the title slot rendered
 * by the same billboard the App Preview uses — the typed title, or the
 * uploaded title logo in its place once it loads, the description capped at
 * two lines under a logo. A customer sees a doubled title (words baked into
 * the art) at once. Geometry stays the shell's FHD numbers in container units.
 */
export function SeriesHeroPreview({ llId, title, descShort, files }: SeriesHeroPreviewProps) {
  const { data: linkConfig } = useSharedLinkConfig();
  const poster = readyPfile(files, PfileTypeId.ListPoster);
  const logo = readyPfile(files, PfileTypeId.TitleLogo);
  const posterUrl = poster && linkConfig ? LinkUtils.getAccountFileUrl(linkConfig, poster.fileId, poster.format) : null;
  const logoUrl = logo && linkConfig ? LinkUtils.getAccountFileUrl(linkConfig, logo.fileId, logo.format) : null;

  const row: AppV1Row = {
    rowId: 'series-hero-preview',
    rowType: 'hero_billboard',
    title: '',
    itemAspect: '16x9',
    titleTreatment: 'none',
    fallback: 'landscape',
    heroStyle: 'card',
    items: [
      {
        idBC: String(llId),
        kind: 'series',
        title,
        descShort: descShort ?? undefined,
        // the file URL is served as-is (no /app/img/ sizing); the preview scales it
        artwork: { card: posterUrl ?? undefined, logo: logoUrl ?? undefined },
      },
    ],
  };

  return (
    <Stack gap={4}>
      <Text fw={600} size="sm">
        Hero preview
      </Text>
      <Text size="xs" c="dimmed">
        How this series reads as the featured title on a TV home screen: your poster beside the title (the
        Card hero style), the title logo in place of the typed title once one is uploaded. Default dark theme;
        each app&apos;s Preview shows its own hero style and theme.
      </Text>
      <div
        style={{
          width: '100%',
          maxWidth: 720,
          overflow: 'hidden',
          borderRadius: 8,
          border: '1px solid var(--mantine-color-gray-3)',
          // the plain ground the band sits on (design ADR-0016: the band paints no ground of its own)
          backgroundColor: PREVIEW_THEME_MIDNIGHT.colors.bgPrimary,
          // FHD geometry inside the billboard is expressed in cqw units against this frame.
          containerType: 'inline-size',
        }}
      >
        <AppTvBillboard cfg={{ theme: PREVIEW_THEME_MIDNIGHT }} row={row} />
      </div>
    </Stack>
  );
}
